import { liveAppData, IconList } from '../../../store/store.ts';
import { devlog } from '../../../logic/utils.ts';
import '../../text-and-lang/as-text.ts';
import '../../overlays/as-tooltip-wrapper.ts';
import '../as-spinner.ts';
import '../../overlays/as-hovercard-wrapper.ts';
import '../../overlays/as-hovercard.ts';
import styleText from './as-button.css?raw';
import { AsButtonBase } from './as-button-base.ts';

/**
 * <as-button>
 *
 * Combined icon + text button. Either or both may be supplied; when only an
 * icon is provided the button renders square (icon-only mode).
 *
 * Attributes
 *   abs-img-path   Absolute path to icon image (highest priority)
 *   icon-set       Icon name from IconList
 *   icon-variant   Variant within icon-set (default: "default")
 *   file-name      Filename relative to commonAppData.imgDir (lowest priority)
 *   black          When present, applies brightness(0) filter to icon
 *   default-text              Button label text
 *   dict-key                  Localization key for button label
 *   toggle-on-default-text    Label text shown when toggled on (requires toggle attribute)
 *   toggle-on-dict-key        Localization key for toggle-on label
 *   name           Identifies the button in as-button-click events
 *   button-id      Sets the id on the inner <button> element
 *   disabled       Disables the button
 *   pending        Shows a spinner instead of icon/text and disables the button
 *   toggle         When present, button toggles on/off state on each click
 *   tooltip-default-text   Tooltip text
 *   tooltip-dict-key       Tooltip localization key
 *   tooltip-placement      Tooltip placement (default: "bottom")
 *   disable-tooltips       Suppress tooltip rendering
 *
 * Events
 *   as-button-click  CustomEvent<{ buttonName, toggledOn, originalEvent }>  bubbles, composed
 *
 * CSS custom properties
 *   --as-btn-gap, --as-btn-bg, --as-btn-hover-bg, --as-btn-color, --as-btn-hover-color
 *   --as-btn-padding, --as-btn-padding-x, --as-btn-padding-y, --as-btn-radius
 *   --as-btn-height, --as-btn-min-width, --as-btn-max-width
 *   --as-btn-font-size, --as-btn-font-weight
 *   --as-btn-img-size, --as-btn-img-hover-filter, --as-btn-img-filter
 *   --as-btn-on-color, --as-btn-focus-color
 */
export class AsButton extends AsButtonBase {
  private static readonly _sheet = (() => {
    const s = new CSSStyleSheet();
    s.replaceSync(styleText);
    return s;
  })();

  static get observedAttributes(): string[] {
    return [
      'abs-img-path', 'file-name', 'icon-set', 'icon-variant', 'black',
      'default-text', 'dict-key', 'toggle-on-default-text', 'toggle-on-dict-key',
      'name', 'button-id', 'disabled', 'pending',
      'tooltip-default-text', 'tooltip-dict-key', 'tooltip-placement', 'disable-tooltips',
      'confirm-on-click', 'confirm-default-text', 'confirm-dict-key',
      'confirm-action-text', 'confirm-action-dict-key', 'confirm-placement',
      'toggle',
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsButtonBase._baseSheet, AsButton._sheet];
    this._render();
  }

  connectedCallback(): void {
    if (this.getAttribute('toggle') === 'on') {
      this.toggledOn = true;
    }
    this._syncToggleState();
    this._attachListeners();
  }

  attributeChangedCallback(name: string, old: string | null, next: string | null): void {
    if (old === next) return;
    if (name === 'toggle') {
      this._syncToggleState();
    } else if (name.startsWith('confirm-')) {
      this._render();
    } else {
      this._render();
      this._attachListeners();
    }
  }

  // ---- Private ----

  private _getIconSrc(): string {
    const absPath = this.getAttribute('abs-img-path');
    if (absPath) return absPath;

    const iconSet     = this.getAttribute('icon-set');
    const iconVariant = this.getAttribute('icon-variant') ?? 'default';
    if (iconSet) {
      const config = IconList[iconSet as keyof typeof IconList];
      if (config) {
        if (iconVariant in config) return config[iconVariant as keyof typeof config];
        devlog(`AsButton: icon variant "${iconVariant}" not found for "${iconSet}"`);
      } else {
        devlog(`AsButton: icon set "${iconSet}" not found in IconList`);
      }
    }

    const name = this.getAttribute('name');
    if (name && !iconSet && !this.hasAttribute('no-default-icon')) {
      const config = IconList[name as keyof typeof IconList];
      if (config && iconVariant in config) return config[iconVariant as keyof typeof config];
    }

    const fileName = this.getAttribute('file-name');
    if (fileName) return `${liveAppData.imgDir}/${fileName}`;

    return '';
  }

  private _hasTooltip(): boolean {
    return !!(
      (this.getAttribute('tooltip-dict-key') || this.getAttribute('tooltip-default-text')) &&
      !this.hasAttribute('disable-tooltips')
    );
  }

  private _render(): void {
    const iconSrc = this._getIconSrc();
    const hasIcon = !!iconSrc;
    const useToggleOn = this.toggledOn && this.hasAttribute('toggle');
    const text    = (useToggleOn && this.hasAttribute('toggle-on-default-text'))
      ? (this.getAttribute('toggle-on-default-text') ?? '')
      : (this.getAttribute('default-text') ?? '');
    const dictKey = (useToggleOn && this.hasAttribute('toggle-on-dict-key'))
      ? this.getAttribute('toggle-on-dict-key')
      : this.getAttribute('dict-key');
    const hasText  = !!(text || dictKey);

    // Host class for icon-only square sizing
    this.classList.toggle('icon-only', hasIcon && !hasText);

    const imgHTML = hasIcon
      ? `<img class="btn-img" src="${iconSrc}" alt="">`
      : '';

    let textHTML = '';
    if (hasText) {
      textHTML = dictKey
        ? `<as-text class="btn-text" dict-key="${dictKey}" default-text="${text}"></as-text>`
        : `<as-text class="btn-text">${text}</as-text>`;
    }

    const isPending  = this.hasAttribute('pending');
    const isDisabled = this.hasAttribute('disabled') || isPending;
    const buttonId   = this.getAttribute('button-id') ?? '';

    const contentHTML = isPending ? '<as-spinner class="btn-spinner"></as-spinner>' : `${imgHTML}${textHTML}`;
    let buttonHTML = `<button id="${buttonId}"${isDisabled ? ' disabled' : ''}>${contentHTML}</button>`;

    if (this._hasTooltip()) {
      const placement = this.getAttribute('tooltip-placement') ?? 'bottom';
      const ttKey  = this.getAttribute('tooltip-dict-key');
      const ttText = this.getAttribute('tooltip-default-text');
      const attrs  = [`show-on-hover`, `placement="${placement}"`];
      if (ttKey)  attrs.push(`dict-key="${ttKey}"`);
      if (ttText) attrs.push(`default-text="${ttText}"`);
      buttonHTML = `<as-tooltip-wrapper ${attrs.join(' ')}>${buttonHTML}</as-tooltip-wrapper>`;
    }

    if (this.hasAttribute('confirm-on-click')) {
      const confirmText = this.getAttribute('confirm-default-text') || 'Are you sure?';
      const confirmDictKey = this.getAttribute('confirm-dict-key');
      const actionText = this.getAttribute('confirm-action-text') || 'Continue';
      const actionDictKey = this.getAttribute('confirm-action-dict-key');
      const placement = this.getAttribute('confirm-placement') || 'bottom';

      buttonHTML = `
        <as-hovercard-wrapper toggle-on-click show-on-hover="false" placement="${placement}" offset="12">
          <div slot="trigger" style="display:contents">${buttonHTML}</div>
          <as-hovercard slot="hovercard">
            <div class="confirm-card">
              <as-text class="confirm-text" ${confirmDictKey ? `dict-key="${confirmDictKey}"` : ''} default-text="${confirmText}"></as-text>
              <as-button class="confirm-action-btn" size="s" ${actionDictKey ? `dict-key="${actionDictKey}"` : ''} default-text="${actionText}"></as-button>
            </div>
          </as-hovercard>
        </as-hovercard-wrapper>
      `;
    }

    this.shadowRoot!.innerHTML = buttonHTML;
    this._attachListeners();
  }

  private _attachListeners(): void {
    const button = this.shadowRoot?.querySelector('button');
    if (button) {
      button.removeEventListener('click', this._handleClick);
      button.addEventListener('click', this._handleClick);
    }

    const confirmBtn = this.shadowRoot?.querySelector('.confirm-action-btn');
    confirmBtn?.addEventListener('as-button-click', (e: Event) => {
      e.stopPropagation();
      this._dispatchConfirmedClick();
      // Close hovercard
      this.shadowRoot!.querySelector('as-hovercard')?.dispatchEvent(new CustomEvent('as-hovercard-close', { bubbles: true, composed: true }));
    });
  }

  private _handleClick = (e: MouseEvent): void => {
    if (this.hasAttribute('confirm-on-click')) {
      // as-hovercard-wrapper handles the click to show confirmation
      return;
    }

    if (this.hasAttribute('toggle')) {
      this.toggledOn = !this.toggledOn;
      this._syncToggleState();
    }
    devlog('AsButton: as-button-click', this.getAttribute('name'));
    this._dispatchButtonClick({
      buttonName: this.getAttribute('name') ?? this.getAttribute('button-id'),
      toggledOn:  this.toggledOn,
      originalEvent: e,
    });
  };

  private _dispatchConfirmedClick() {
    this._dispatchButtonClick({
      buttonName: this.getAttribute('name') ?? this.getAttribute('button-id'),
      toggledOn:  this.toggledOn
    });
  }

  protected _syncToggleState(): void {
    if (this.hasAttribute('toggle') && (this.hasAttribute('toggle-on-default-text') || this.hasAttribute('toggle-on-dict-key'))) {
      this._render();
    }
    const button = this.shadowRoot?.querySelector('button');
    if (!button) return;
    if (this.hasAttribute('toggle')) {
      button.classList.toggle('on', this.toggledOn);
    } else {
      button.classList.remove('on');
    }
  }
}

if (!customElements.get('as-button')) {
  customElements.define('as-button', AsButton);
} else {
  console.warn('as-button already defined');
}
